import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { supabase } from '../../lib/supabaseClient';
import Spinner from '../Atoms/Spinner';

// ─── Constants ──────────────────────────────────────────────
const EVENT_ID = '67590da5499bcc995c210b40';

const KV_BASE = '/assets/fd100-kit/communication-kit/key-visuals';
const INV_BASE = '/assets/fd100-kit/communication-kit/invitation';
// Deployed by FTP (gitignored) together with photos.json, so new photos need no rebuild.
// photos.json: { "zip": "archive.zip" (optional), "photos": ["a.jpg", { "file": "b.jpg", "preview": "b-preview.jpg" }] }
const PHOTOS_BASE = '/assets/fd100-kit/photos';

// Each visual exists in FR and EN. Preview follows the UI language; both languages are downloadable.
const KEY_VISUALS = [
    { label: 'Facebook', file: 'Format-Facebook' },
    { label: 'LinkedIn', file: 'Format-LinkedIn' },
    { label: 'Instagram Portrait', file: 'Format-Portrait-Instagram' },
    { label: 'Instagram Story', file: 'Format-Story-Instagram' },
].map(({ label, file }) => ({
    label,
    fr: {
        preview: `${KV_BASE}/${file}-preview.jpg`,
        jpg: `${KV_BASE}/${file}.jpg`,
        pdf: `${KV_BASE}/${file}.pdf`,
    },
    en: {
        preview: `${KV_BASE}/${file}-EN-preview.jpg`,
        jpg: `${KV_BASE}/${file}-EN.jpg`,
        pdf: null,
    },
}));

const INVITATIONS = [
    {
        label: 'Portrait',
        fr: {
            preview: `${INV_BASE}/E-invitation-portrait-preview.jpg`,
            jpg: `${INV_BASE}/E-invitation-portrait.jpg`,
            pdf: `${INV_BASE}/E-invitation-portrait.pdf`,
        },
        en: {
            preview: `${INV_BASE}/E-invitation-portrait-EN-preview.jpg`,
            jpg: `${INV_BASE}/E-invitation-portrait-EN.jpg`,
            pdf: `${INV_BASE}/E-invitation-portrait-EN.pdf`,
        },
    },
    {
        label: 'Paysage',
        fr: {
            preview: `${INV_BASE}/E-invitation-paysage-preview.jpg`,
            jpg: `${INV_BASE}/E-invitation-paysage.jpg`,
            pdf: null,
        },
        en: {
            preview: `${INV_BASE}/E-invitation-paysage-EN-preview.jpg`,
            jpg: `${INV_BASE}/E-invitation-paysage-EN.jpg`,
            pdf: null,
        },
    },
];

const LANGS = [
    { code: 'fr', tag: 'FR' },
    { code: 'en', tag: 'EN' },
];

// ─── Styled Components ──────────────────────────────────────
const Container = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 20px;
`;

const KitHeader = styled.div`
    text-align: center;
    margin-bottom: 50px;
`;

const LogoImg = styled.img`
    max-width: 180px;
    max-height: 100px;
    object-fit: contain;
    margin-bottom: 16px;
`;

const Subtitle = styled.div`
    font-size: 20px;
    color: #555;
    margin-top: 8px;
`;

const PieceBadge = styled.div`
    text-align: center;
    font-size: 1.05rem;
    font-weight: 600;
    color: rgb(24, 51, 189);
    background: #f0f4ff;
    border: 1px solid #d0d8ff;
    border-radius: 8px;
    padding: 14px 20px;
    margin-bottom: 32px;
    line-height: 1.5;
`;

const PieceName = styled.span`
    display: block;
    font-size: 1.15rem;
`;

const DesignerName = styled.span`
    display: block;
    font-size: 0.95rem;
    font-weight: 400;
    color: #555;
    margin-top: 4px;
`;

// ─── Accordion ──────────────────────────────────────────────
const AccordionWrapper = styled.div`
    margin-bottom: 8px;
    border-bottom: 1px solid #ddd;
`;

const AccordionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 0;
    cursor: pointer;
    user-select: none;

    &:hover { opacity: 0.7; }
`;

const AccordionLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const SectionNumber = styled.span`
    font-size: 22px;
    font-weight: 700;
    color: rgb(24, 51, 189);
    min-width: 30px;
`;

const SectionTitle = styled.span`
    font-size: 20px;
    font-weight: 600;
    color: #222;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const ChevronIcon = styled.span`
    font-size: 20px;
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const AccordionContent = styled.div`
    max-height: ${props => props.isOpen ? '10000px' : '0'};
    overflow: hidden;
    transition: max-height 0.4s ease, opacity 0.3s ease, padding 0.3s ease;
    opacity: ${props => props.isOpen ? 1 : 0};
    padding: ${props => props.isOpen ? '0 0 30px 0' : '0'};
`;

// ─── Content ────────────────────────────────────────────────
const VisualsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const VisualImageCard = styled.div`
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
    text-align: center;
    display: flex;
    flex-direction: column;

    .image-wrapper {
        position: relative;

        img {
            width: 100%;
            height: auto;
            display: block;
        }
    }

    .card-footer {
        padding: 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;
    }

    .card-label {
        font-weight: 600;
        font-size: 13px;
        color: #555;
    }
`;

const DownloadBtn = styled.a`
    display: inline-block;
    padding: 8px 16px;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    text-decoration: none;
    border-radius: 5px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
        color: white;
        text-decoration: none;
    }

    &:visited, &:active, &:focus {
        color: white;
        text-decoration: none;
    }
`;

const DownloadBtnRow = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
`;

const LangTag = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: #888;
    margin-left: 6px;
`;

const DownloadGroups = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-end;
`;

const PressCard = styled.div`
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 24px;
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
    }
`;

const PressIcon = styled.div`
    font-size: 40px;
    min-width: 60px;
    text-align: center;
`;

const PressInfo = styled.div`
    flex: 1;

    h4 {
        margin: 0 0 6px 0;
        font-size: 16px;
        color: #222;
    }

    p {
        margin: 0;
        font-size: 14px;
        color: #666;
        line-height: 1.5;
    }
`;

const StaticTextBlock = styled.div`
    background: #f9f9f9;
    border-left: 3px solid rgb(24, 51, 189);
    padding: 16px 20px;
    border-radius: 0 6px 6px 0;
    color: #444;
    font-size: 15px;
    line-height: 1.6;
    margin-bottom: 16px;
`;

const PhotosGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const PhotoCard = styled.div`
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    img {
        width: 100%;
        aspect-ratio: 3 / 2;
        object-fit: cover;
        display: block;
    }

    .card-footer {
        padding: 8px;
        display: flex;
        justify-content: flex-end;
    }
`;


// ─── Exhibition photos ─────────────────────────────────────
const ExhibitionPhotos = ({ isFr }) => {
    const [manifest, setManifest] = useState(undefined);

    useEffect(() => {
        fetch(`${PHOTOS_BASE}/photos.json`, { cache: 'no-cache' })
            .then(res => (res.ok ? res.json() : null))
            .then(data => {
                const list = Array.isArray(data) ? data : data?.photos;
                if (!Array.isArray(list) || list.length === 0) return setManifest(null);
                setManifest({
                    zip: Array.isArray(data) ? null : data.zip,
                    photos: list.map(p => (typeof p === 'string' ? { file: p } : p)),
                });
            })
            .catch(() => setManifest(null));
    }, []);

    if (manifest === undefined) return <Spinner />;

    if (!manifest) {
        return (
            <StaticTextBlock>
                {isFr
                    ? "Les photographies de l'exposition et du vernissage seront disponibles prochainement."
                    : 'Photographs of the exhibition and the opening will be available soon.'
                }
            </StaticTextBlock>
        );
    }

    return (
        <>
            <StaticTextBlock>
                {isFr
                    ? "Retrouvez ci-dessous les photographies de l'exposition et du vernissage, à partager sur vos réseaux sociaux."
                    : 'Find below the photographs of the exhibition and the opening, to share on your social media channels.'
                }
            </StaticTextBlock>
            {manifest.zip && (
                <PressCard>
                    <PressIcon>📷</PressIcon>
                    <PressInfo>
                        <h4>{isFr ? 'Toutes les photographies' : 'All photographs'}</h4>
                        <p>{isFr ? `${manifest.photos.length} photos · archive ZIP` : `${manifest.photos.length} photos · ZIP archive`}</p>
                    </PressInfo>
                    <DownloadBtn href={`${PHOTOS_BASE}/${manifest.zip}`} download={manifest.zip}>
                        {isFr ? 'Télécharger ZIP' : 'Download ZIP'}
                    </DownloadBtn>
                </PressCard>
            )}
            <PhotosGrid>
                {manifest.photos.map(({ file, preview }) => (
                    <PhotoCard key={file}>
                        <a href={`${PHOTOS_BASE}/${file}`} target="_blank" rel="noopener noreferrer">
                            <img src={`${PHOTOS_BASE}/${preview || file}`} alt="" loading="lazy" />
                        </a>
                        <div className="card-footer">
                            <DownloadBtn href={`${PHOTOS_BASE}/${file}`} download={file}>
                                {isFr ? 'Télécharger' : 'Download'}
                            </DownloadBtn>
                        </div>
                    </PhotoCard>
                ))}
            </PhotosGrid>
        </>
    );
};


// ─── Bilingual download card ───────────────────────────────
const BilingualVisualCard = ({ item, lang, filePrefix }) => (
    <VisualImageCard>
        <div className="image-wrapper">
            <img src={item[lang].preview} alt={`${filePrefix} ${item.label}`} />
        </div>
        <div className="card-footer">
            <span className="card-label">{item.label}</span>
            <DownloadGroups>
                {LANGS.map(({ code, tag }) => (
                    <DownloadBtnRow key={code}>
                        <LangTag>{tag}</LangTag>
                        <DownloadBtn
                            href={item[code].jpg}
                            download={`${filePrefix}-${item.label.replace(/\s/g, '-')}-${tag}.jpg`}
                        >
                            JPG
                        </DownloadBtn>
                        {item[code].pdf && (
                            <DownloadBtn
                                href={item[code].pdf}
                                download={`${filePrefix}-${item.label.replace(/\s/g, '-')}-${tag}.pdf`}
                            >
                                PDF
                            </DownloadBtn>
                        )}
                    </DownloadBtnRow>
                ))}
            </DownloadGroups>
        </div>
    </VisualImageCard>
);

// ─── Accordion Item ─────────────────────────────────────────
const AccordionItem = ({ number, title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <AccordionWrapper>
            <AccordionHeader onClick={() => setIsOpen(!isOpen)}>
                <AccordionLeft>
                    <SectionNumber>{number}</SectionNumber>
                    <SectionTitle>{title}</SectionTitle>
                </AccordionLeft>
                <ChevronIcon isOpen={isOpen}>&#9660;</ChevronIcon>
            </AccordionHeader>
            <AccordionContent isOpen={isOpen}>
                {children}
            </AccordionContent>
        </AccordionWrapper>
    );
};

// ─── Main Component ─────────────────────────────────────────
const CommunicationKitPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const { i18n } = useTranslation('common');
    const isFr = i18n.language !== 'en';
    const lang = isFr ? 'fr' : 'en';

    const [pieceData, setPieceData] = useState(null);
    const [loading, setLoading] = useState(!!token);
    const [error, setError] = useState(null);

    // Fetch piece data if token is present
    useEffect(() => {
        if (!token) return;
        const fetchPiece = async () => {
            setLoading(true);
            try {
                const { data, error: sbError } = await supabase
                    .from('fd100_expo_finale_pieces')
                    .select('nom_piece, designer_names, fabricant, partenaire_name, photos, type')
                    .eq('event_id', EVENT_ID)
                    .eq('token', token)
                    .single();

                if (sbError || !data) {
                    setError(isFr ? 'Lien invalide ou expiré.' : 'Invalid or expired link.');
                } else {
                    setPieceData(data);
                }
            } catch {
                setError(isFr ? 'Erreur de chargement.' : 'Loading error.');
            }
            setLoading(false);
        };
        fetchPiece();
    }, [token, isFr]);

    if (loading) {
        return (
            <Container style={{ textAlign: 'center', paddingTop: 80 }}>
                <Spinner />
            </Container>
        );
    }

    if (error) {
        return (
            <Container style={{ textAlign: 'center', paddingTop: 80 }}>
                <p style={{ color: '#c0392b', fontSize: 18 }}>{error}</p>
            </Container>
        );
    }

    const hasToken = !!pieceData;

    return (
        <Container>
            <KitHeader>
                <LogoImg
                    src="/assets/fd100-kit/logos/SD25-FD100-Logo-Story.png"
                    alt="Le FRENCH DESIGN 100"
                />
                <h1 style={{ fontSize: 26, fontWeight: 700, color: '#1a1a2e', margin: '0 0 4px' }}>
                    {isFr ? 'Kit de Communication' : 'Communication Kit'}
                </h1>
                <Subtitle>
                    {isFr
                        ? "Exposition « Hospitalité lyrique, l'émotion dans l'art de recevoir »"
                        : "Exhibition \"Hospitalité lyrique, l'émotion dans l'art de recevoir\""
                    }
                </Subtitle>
            </KitHeader>

            {hasToken && (
                <PieceBadge>
                    <PieceName>{pieceData.nom_piece}</PieceName>
                    <DesignerName>
                        {pieceData.designer_names}
                        {pieceData.fabricant ? ` — ${pieceData.fabricant}` : ''}
                    </DesignerName>
                </PieceBadge>
            )}

            {/* Section 1: Key Visuals */}
            <AccordionItem
                number="1"
                title={isFr ? 'Le Key Visual' : 'Key Visual'}
                defaultOpen={false}
            >
                <StaticTextBlock>
                    {isFr
                        ? "Retrouvez ci-dessous les visuels clés de l'exposition dans différents formats adaptés à vos réseaux sociaux, en français et en anglais."
                        : 'Find below the key visuals of the exhibition in various formats suited for your social media channels, in French and English.'
                    }
                </StaticTextBlock>
                <VisualsGrid>
                    {KEY_VISUALS.map((kv) => (
                        <BilingualVisualCard key={kv.label} item={kv} lang={lang} filePrefix="FD100-KV" />
                    ))}
                </VisualsGrid>
            </AccordionItem>

            {/* Section 2: Communiqué de presse */}
            <AccordionItem
                number="2"
                title={isFr ? 'Le Communiqué de presse' : 'Press Release'}
            >
                <PressCard>
                    <PressIcon>📄</PressIcon>
                    <PressInfo>
                        <h4>{isFr ? 'Communiqué de presse' : 'Press Release'}</h4>
                        <p>
                            {isFr
                                ? "Hospitalité lyrique, l'émotion dans l'art de recevoir"
                                : "Hospitalité lyrique, the emotion in the art of hosting"
                            }
                        </p>
                    </PressInfo>
                    <DownloadBtn
                        href="/assets/fd100-kit/communication-kit/presse/Communique-de-presse-Hospitalite-lyrique.pdf"
                        download="Communique-de-presse-Hospitalite-lyrique.pdf"
                    >
                        {isFr ? 'Télécharger PDF' : 'Download PDF'}
                    </DownloadBtn>
                </PressCard>
            </AccordionItem>

            {/* Section 3: Dossier de presse */}
            <AccordionItem
                number="3"
                title={isFr ? 'Le Dossier de presse' : 'Press Kit'}
            >
                <PressCard>
                    <PressIcon>📰</PressIcon>
                    <PressInfo>
                        <h4>{isFr ? 'Dossier de presse — Français' : 'Press Kit — French'}</h4>
                        <p>Hospitalité lyrique, l'émotion dans l'art de recevoir · Septembre 2026</p>
                    </PressInfo>
                    <DownloadBtn
                        href="/assets/fd100-kit/communication-kit/presse/Dossier-de-presse-Hospitalite-lyrique-FR.pdf"
                        download="Dossier-de-presse-Hospitalite-lyrique-FR.pdf"
                    >
                        {isFr ? 'Télécharger PDF' : 'Download PDF'}
                    </DownloadBtn>
                </PressCard>
                <PressCard>
                    <PressIcon>📰</PressIcon>
                    <PressInfo>
                        <h4>{isFr ? 'Dossier de presse — Anglais' : 'Press Kit — English'}</h4>
                        <p>Lyrical Hospitality, Emotion in the Art of Hospitality · September 2026</p>
                    </PressInfo>
                    <DownloadBtn
                        href="/assets/fd100-kit/communication-kit/presse/Press-Kit-Lyrical-Hospitality-EN.pdf"
                        download="Press-Kit-Lyrical-Hospitality-EN.pdf"
                    >
                        {isFr ? 'Télécharger PDF' : 'Download PDF'}
                    </DownloadBtn>
                </PressCard>
            </AccordionItem>

            {/* Section 4: Invitation */}
            <AccordionItem
                number="4"
                title={isFr ? "L'Invitation" : 'Invitation'}
            >
                <StaticTextBlock>
                    {isFr
                        ? "Retrouvez ci-dessous l'e-invitation de l'exposition en format portrait et paysage, en français et en anglais, à partager auprès de vos contacts et sur vos réseaux sociaux."
                        : 'Find below the exhibition e-invitation in portrait and landscape formats, in French and English, to share with your contacts and on your social media channels.'
                    }
                </StaticTextBlock>
                <VisualsGrid>
                    {INVITATIONS.map((inv) => (
                        <BilingualVisualCard key={inv.label} item={inv} lang={lang} filePrefix="FD100-E-invitation" />
                    ))}
                </VisualsGrid>
            </AccordionItem>

            {/* Section 5: Photographies */}
            <AccordionItem
                number="5"
                title={isFr ? "Photographies de l'exposition et du vernissage" : 'Photographs of the exhibition and the opening'}
            >
                <ExhibitionPhotos isFr={isFr} />
            </AccordionItem>
        </Container>
    );
};

export default CommunicationKitPage;
